import React from 'react';
import { ShoppingBag, Plus, Minus, Trash2 } from 'lucide-react';

const CartPage = ({ cartItems, updateQuantity, removeFromCart, setCurrentPage }) => {
  const shippingFee = 10;
  const freeShippingThreshold = 200;

  const calculateSubtotal = () => {
    return cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const subtotal = calculateSubtotal();
  const shippingCost = subtotal > freeShippingThreshold ? 0 : shippingFee;
  const total = subtotal + shippingCost;

  return (
    <section className="bg-gray-50 py-16 font-['Cairo'] lg:py-24">
      <div className="container mx-auto px-4">
        <div className="mb-12 text-center">
          <div className="mb-4 flex items-center justify-center gap-2">
            <ShoppingBag className="h-6 w-6 text-[#10B981]" />
            <span className="font-semibold text-[#10B981]">سلة التسوق</span>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 lg:text-4xl">محتويات سلتك</h2>
        </div>

        {cartItems.length === 0 ? (
          <div className="mx-auto max-w-xl rounded-2xl border-2 border-dashed border-gray-300 bg-white p-12 text-center shadow-sm">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#10B981]/10">
              <ShoppingBag className="h-7 w-7 text-[#10B981]" />
            </div>
            <p className="mb-6 text-xl text-gray-600">سلتك فارغة حالياً.</p>
            <button
              onClick={() => setCurrentPage('home')}
              className="rounded-xl bg-[#10B981] px-8 py-3.5 font-bold text-white shadow-sm shadow-[#10B981]/20 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#0ea173] hover:shadow-lg hover:shadow-[#10B981]/25"
            >
              العودة للتسوق
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            {/* جدول المنتجات في السلة */}
            <div className="space-y-4 lg:col-span-2">
              {cartItems.map((item) => (
                <div
                  key={item.id}
                  className="flex flex-col items-center gap-4 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-100 transition-shadow duration-300 hover:shadow-md md:flex-row"
                >
                  {/* صورة المنتج */}
                  <div className="h-24 w-24 flex-shrink-0 overflow-hidden rounded-xl bg-gray-50 ring-1 ring-gray-100">
                    <img src={item.imageSrc} alt={item.name} className="h-full w-full object-contain p-2" />
                  </div>

                  {/* تفاصيل المنتج */}
                  <div className="flex-1 text-center md:text-right">
                    <h3 className="line-clamp-2 text-lg font-bold text-gray-900">{item.name}</h3>
                    <p className="mb-1 text-sm text-gray-500">{item.brand}</p>
                    <p className="mt-2 text-xl font-bold text-[#10B981]">{item.price.toFixed(2)} ر.س</p>
                  </div>

                  {/* التحكم في الكمية */}
                  <div className="flex items-center gap-3 rounded-xl bg-gray-50 p-1.5">
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-gray-600 shadow-sm transition-all duration-200 hover:text-[#10B981] active:scale-95"
                      aria-label="تقليل الكمية"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="w-6 text-center text-lg font-bold text-gray-900">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-gray-600 shadow-sm transition-all duration-200 hover:text-[#10B981] active:scale-95"
                      aria-label="زيادة الكمية"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>

                  {/* زر الإزالة */}
                  <button
                    onClick={() => removeFromCart(item.id)}
                    className="rounded-full p-2.5 text-red-500 transition-colors duration-200 hover:bg-red-50 hover:text-red-600"
                    aria-label="إزالة المنتج"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>

            {/* ملخص السلة */}
            <div className="h-fit rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100 lg:col-span-1">
              <h3 className="mb-4 text-xl font-bold text-gray-900">ملخص الطلب</h3>
              <div className="space-y-4">
                <div className="flex justify-between text-gray-700">
                  <span>المجموع الفرعي:</span>
                  <span>{subtotal.toFixed(2)} ر.س</span>
                </div>
                <div className="flex justify-between text-gray-700">
                  <span>الشحن:</span>
                  <span className={shippingCost === 0 ? 'font-bold text-[#10B981]' : 'text-gray-700'}>
                    {shippingCost === 0 ? 'مجاني' : `${shippingCost.toFixed(2)} ر.س`}
                  </span>
                </div>
                {shippingCost !== 0 && (
                  <div className="rounded-lg bg-[#10B981]/5 p-3 text-center text-sm text-gray-500">
                    <p>أضف منتجات بقيمة {(freeShippingThreshold - subtotal).toFixed(2)} ر.س لتحصل على شحن مجاني.</p>
                  </div>
                )}
                <div className="my-4 h-px bg-gray-200"></div>
                <div className="flex justify-between text-xl font-bold text-gray-900">
                  <span>الإجمالي:</span>
                  <span>{total.toFixed(2)} ر.س</span>
                </div>
              </div>
              <button
                onClick={() => setCurrentPage('checkout')}
                className="mt-6 w-full rounded-xl bg-[#10B981] py-4 font-bold text-white shadow-sm shadow-[#10B981]/20 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#0ea173] hover:shadow-lg hover:shadow-[#10B981]/25 active:translate-y-0"
              >
                إتمام عملية الشراء
              </button>
              <button
                onClick={() => setCurrentPage('home')}
                className="mt-3 w-full rounded-xl border-2 border-gray-200 py-4 font-bold text-gray-700 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#10B981] hover:text-[#10B981]"
              >
                العودة للتسوق
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default CartPage;
